/* =====================================================================================
   An error boundary, so one broken component cannot take the page with it.

   Twice now a throw inside a React callback has unmounted the entire tree and left a white
   screen: once from a snapshot that returned a new array on every call, once from a
   non-null assertion evaluated inside a state updater after the pointer was released. Both
   were real bugs and both are fixed, but the failure mode is the problem. A user seeing a
   blank page cannot tell a crash from a network failure from an empty database, and neither
   can whoever they report it to.

   A boundary converts that into a legible, contained failure: the rest of the page survives,
   the panel says what broke, and the console still carries the stack.
   ===================================================================================== */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props { children: ReactNode; what: string }
interface State { error: Error | null }

export class Boundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The stack is what a developer needs and is deliberately not put on screen.
    console.error(`[forum] ${this.props.what} crashed:`, error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="boundary card" role="alert">
        <strong>{this.props.what} could not be displayed.</strong>
        <p>
          The rest of this page is unaffected. The technical detail is in the browser console;
          reloading usually clears it, and if it does not, this is worth reporting.
        </p>
        <code>{this.state.error.message}</code>
        <button className="btn-compose" onClick={() => this.setState({ error: null })}>
          Try again
        </button>
      </div>
    );
  }
}
